import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usage as fetchUsage } from '../api/endpoints';
import { PageHeader } from '../components/AppShell';
import { CreditTopUpUnavailableNotice } from '../components/CreditTopUpModal';
import { EmptyState, ErrorNotice, LoadingRows, SectionTitle } from '../components/Feedback';
import { Mono } from '../components/Pills';
import { formatRelative, formatTimestamp } from '../lib/format';
import { useAuth, useResource, useToast } from '../state';

export function shouldShowExhaustedCreditWarning({
  loading,
  error,
  creditsRemaining,
}: {
  loading: boolean;
  error: Error | null;
  creditsRemaining: number | undefined;
}): boolean {
  return !loading && !error && creditsRemaining !== undefined && creditsRemaining <= 0;
}

export function BillingPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { subscription } = useAuth();

  const usage = useResource(() => fetchUsage(), [], { live: ['run.finished'] });

  // Old checkout return URLs are not payment confirmation. Strip them and show
  // only a neutral notice so stale links cannot claim credits were added.
  useEffect(() => {
    if (searchParams.has('topup')) {
      toast.info(
        'Legacy checkout link',
        'Payment status cannot be confirmed from this link. Credit purchases are unavailable until live payment integration is enabled.',
      );
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('topup');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams, toast]);

  const data = usage.data;
  const remaining = data?.creditsRemaining;
  const monthly = data?.creditsMonthly;
  const hasBalance = typeof remaining === 'number';
  const hasMonthlyCredits = typeof monthly === 'number' && monthly > 0;
  const percent = hasBalance && hasMonthlyCredits
    ? Math.min(100, Math.max(0, (remaining / monthly) * 100))
    : 0;
  const frontendRunsLeft = hasBalance ? Math.floor(remaining / 0.5) : null;
  const subscriptionNeedsAttention = subscription?.status === 'past_due' || subscription?.status === 'incomplete';

  return (
    <div className="page">
      <PageHeader
        title="Billing & Credits"
        subtitle="Review your balance, subscription, and usage history."
      />

      <CreditTopUpUnavailableNotice />

      {(subscriptionNeedsAttention || shouldShowExhaustedCreditWarning({ loading: usage.loading, error: usage.error, creditsRemaining: remaining })) ? (
        <aside
          className="notice notice--error"
          role="alert"
          aria-live="assertive"
          style={{ marginBottom: '16px' }}
        >
          <div>
            <h4 className="notice__title">
              {subscription?.status === 'past_due'
                ? '⚠️ Subscription Past Due'
                : subscription?.status === 'incomplete'
                  ? '⚠️ Payment Incomplete'
                  : '⚠️ Credit Balance Exhausted'}
            </h4>
            <p className="notice__message">
              {subscriptionNeedsAttention
                ? 'The server blocks new test runs while this subscription is past due or incomplete. Billing changes are not available here yet.'
                : 'The server blocks new test runs while the credit balance is exhausted. Paid top-ups are not available yet.'}
            </p>
          </div>
        </aside>
      ) : null}

      {usage.error ? <ErrorNotice error={usage.error} onRetry={usage.reload} /> : null}
      {usage.loading ? <LoadingRows rows={3} label="Loading billing information" /> : null}

      {data ? (
        <>
          {/* Balance Overview */}
          <section className="panel">
            <SectionTitle>Credit Balance</SectionTitle>
            <dl className="fact-grid">
              <div>
                <dt>Credits Remaining</dt>
                <dd className="num" style={{ fontSize: '20px', fontWeight: 600 }}>
                  {hasBalance ? remaining : '-'}{' '}
                  <span className="muted" style={{ fontSize: '13px', fontWeight: 400 }}>
                    {hasMonthlyCredits ? `of ${monthly} monthly` : null}
                  </span>
                </dd>
              </div>
              <div>
                <dt>Current Plan</dt>
                <dd style={{ textTransform: 'capitalize' }}>
                  <strong>{data.plan}</strong>
                </dd>
              </div>
              <div>
                <dt>Subscription Status</dt>
                <dd style={{ textTransform: 'capitalize' }}>{subscription?.status ?? 'Unavailable'}</dd>
              </div>
              <div>
                <dt>Standard Rate</dt>
                <dd className="num">$0.0285 / credit</dd>
              </div>
              <div>
                <dt>Runs Remaining</dt>
                <dd className="num">{frontendRunsLeft === null ? '-' : `≈ ${frontendRunsLeft} frontend`}</dd>
              </div>
            </dl>

            <div
              className="meter"
              role="img"
              aria-label={!hasBalance ? 'Credit balance unavailable' : hasMonthlyCredits ? `${remaining} of ${monthly} credits remaining` : `${remaining} credits remaining`}
              style={{ marginTop: '14px' }}
            >
              <span
                className="meter__fill"
                style={{
                  width: `${percent}%`,
                  background:
                    hasBalance && remaining <= 5
                      ? 'var(--fail)'
                      : percent <= 25
                      ? 'var(--warn)'
                      : 'var(--accent)',
                }}
              />
            </div>
            {!usage.loading && !usage.error && hasBalance && remaining <= 5 ? (
              <p
                style={{
                  margin: '8px 0 0',
                  fontSize: '13px',
                  color: 'var(--fail)',
                  fontWeight: 500,
                }}
              >
                ⚠️ Your credit balance is critically low. New runs will be rejected when credits reach 0.
              </p>
            ) : null}
          </section>

          {/* Cost Estimates / Rate Card */}
          <section className="panel">
            <SectionTitle>Estimated Cost per Action</SectionTitle>
            <p className="muted" style={{ margin: '0 0 16px' }}>
              Justloop charges strictly per action completed, never per idle worker seconds.
            </p>

            <div className="table-wrap">
              <table className="table table--compact table--stacked-mobile">
                <thead>
                  <tr>
                    <th scope="col">Action</th>
                    <th scope="col" className="num">Credits</th>
                    <th scope="col" className="num">Est. Cost (USD)</th>
                    <th scope="col">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td data-label="Action"><strong>Frontend Run (Browser)</strong></td>
                    <td data-label="Credits" className="num">0.5</td>
                    <td data-label="Est. Cost" className="num">$0.0142</td>
                    <td data-label="Notes" className="muted">Full Playwright browser session with artifacts and video</td>
                  </tr>
                  <tr>
                    <td data-label="Action"><strong>Backend Run (HTTP)</strong></td>
                    <td data-label="Credits" className="num">0.2</td>
                    <td data-label="Est. Cost" className="num">$0.0057</td>
                    <td data-label="Notes" className="muted">Direct API verification without browser overhead</td>
                  </tr>
                  <tr>
                    <td data-label="Action"><strong>AI Plan Generation</strong></td>
                    <td data-label="Credits" className="num">2.0</td>
                    <td data-label="Est. Cost" className="num">$0.0570</td>
                    <td data-label="Notes" className="muted">Synthesizing test steps from PRD/notes (waived when BYOK is provided)</td>
                  </tr>
                  <tr>
                    <td data-label="Action"><strong>PRD Embedding</strong></td>
                    <td data-label="Credits" className="num">0.5</td>
                    <td data-label="Est. Cost" className="num">$0.0142</td>
                    <td data-label="Notes" className="muted">Indexing document context for agent retrieval</td>
                  </tr>
                  <tr>
                    <td data-label="Action"><strong>Flaky Repeats</strong></td>
                    <td data-label="Credits" className="num">0.5</td>
                    <td data-label="Est. Cost" className="num">$0.0142</td>
                    <td data-label="Notes" className="muted">Multi-run flake detection verification</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Credit Ledger Table */}
          <section className="panel">
            <SectionTitle>Usage & Transaction History</SectionTitle>
            <p className="muted" style={{ margin: '0 0 14px' }}>
              Every charge, refund, and top-up grant is recorded immutably in your credit ledger.
            </p>

            {data.ledger.length === 0 ? (
              <EmptyState title="No transactions recorded yet">
                No credit activity has occurred for this organization yet.
              </EmptyState>
            ) : (
              <div className="table-wrap">
                <table className="table table--compact table--stacked-mobile">
                  <caption className="visually-hidden">Recent credit ledger entries</caption>
                  <thead>
                    <tr>
                      <th scope="col">Date & Time</th>
                      <th scope="col" className="num">Delta</th>
                      <th scope="col">Description</th>
                      <th scope="col">Run ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ledger.map((entry) => (
                      <tr key={entry.id}>
                        <td data-label="Date & Time">
                          <span title={formatTimestamp(entry.at)}>
                            {formatRelative(entry.at)}
                          </span>
                        </td>
                        <td data-label="Delta" className="num">
                          <span
                            className={entry.delta > 0 ? 'delta delta--up' : 'delta delta--down'}
                            style={{
                              color: entry.delta > 0 ? 'var(--pass)' : 'inherit',
                              fontWeight: entry.delta > 0 ? 600 : 400,
                            }}
                          >
                            {entry.delta > 0 ? `+${entry.delta}` : entry.delta}
                          </span>
                        </td>
                        <td data-label="Description">{entry.reason}</td>
                        <td data-label="Run ID">
                          {entry.runId ? (
                            <Mono>{entry.runId.slice(0, 10)}…</Mono>
                          ) : (
                            <span className="muted">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      ) : null}

    </div>
  );
}
